import React, { useState, useEffect } from 'react';
import Iconfont from '@base/lib/Iconfont';
import ListProductImage from '@base/snippets/ListProduct/ListProductImage';
import ListProductName from '@base/snippets/ListProduct/ListProductName';
import cn from 'classnames';
import { useStore } from 'base/store';
import { IBonusProduct, IBonusVariant } from '@base/cart/interface';
import styles from './index.module.less';
import { IconPoints } from '@pro/common/IconPoints';
import { useTrans } from 'base/utils/i18n';

type IProps = {
    bonusProduct: IBonusProduct;
    miniCartLinked?: boolean;
    onBonusProdSelect: any;
    onQuickView?: (recordId: string) => void;
    showType?: 'm' | 'pc'; // pc 中miniCart 展示为m 类型
}

const checkVariantOnSale = (variant: IBonusVariant): boolean => {
    return !!(variant.isOnSale && variant.isStock)
}

const BonusProdItem: React.FC<IProps> = (props) => {
    // 选中的 variant Option
    const [selectedVariant, setSelectedVariant] = useState<IBonusVariant>()
    // 该 BonusProdItem 是否已被
    const [isSelected, setIsSelected] = useState<boolean>(false)
    const { t } = useTrans();

    const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';

    const {
        onBonusProdSelect,
        miniCartLinked = false,
        bonusProduct,
        onQuickView,
        showType = isPhone ? 'm' : 'pc',
    } = props;

    const { miniCartStore } = useStore();

    const {
        variants = [],
        optionType = 'image',
        canSelect = false,
        spendToUnlockBonusText
    } = bonusProduct;

    // 选中/取消选中
    const onSelectedBonusHandle = async (variant: IBonusVariant, replaceVariant?: IBonusVariant) => {
        await onBonusProdSelect?.(variant, replaceVariant)
        miniCartLinked && miniCartStore.reloadByBonusList();
    }

    // 切换选项
    const onSelectOption = (item: IBonusVariant) => {
        setSelectedVariant({
            ...item,
            tradeIn: {
                ...item.tradeIn,
                hasSelect: isSelected, // 切换时 保留当前是否已选状态
            }
        })

        if (!!isSelected) {
            onSelectedBonusHandle(item, selectedVariant).then()
        }
    }

    const initSelectOnHandle = () => {
        // bonusItem 选中
        let _selectedVariant = variants.find((variant) => variant.tradeIn.hasSelect)
        setIsSelected(!!_selectedVariant)

        if (!_selectedVariant) {
            if (!selectedVariant) {
                // 取在售的
                _selectedVariant = variants.find((variant) => checkVariantOnSale(variant))
                if (!_selectedVariant) {
                    // 第一个
                    _selectedVariant = variants[0]
                }
            } else {
                // 重新取之前选中的variant的数据
                _selectedVariant = variants.find((variant) => variant.variantId == selectedVariant.variantId)
            }
        }

        setSelectedVariant(_selectedVariant)
    }

    useEffect(() => {
        initSelectOnHandle()
    }, [bonusProduct])

    const isSelectedOn = canSelect && selectedVariant?.tradeIn?.hasSelect
    const isStock = selectedVariant?.isStock && selectedVariant?.isOnSale

    return !selectedVariant ? null : (
        <div className={cn(
            "bonus-item",
            styles['bonus-item'],
            showType == 'm' && styles['m'],
        )}>
            {/* pc 勾选框 */}
            {showType == 'pc' && (
                <div
                    className={cn('ipt')}
                    onClick={() => isStock && onSelectedBonusHandle(selectedVariant)}
                >
                    <Iconfont
                        className={cn(
                            isSelectedOn ? 'icon-select1' : 'icon-radio-unchecked',
                            { 'out-of-stock': !isStock }
                        )}
                    />
                </div>
            )}

            {/* 图片 */}
            <ListProductImage
                className={'pointer g-product-img'}
                product={selectedVariant}
                imageSize={80}
                fixed
                showTag={false}
                background={false}
                canJump={false}
                onClick={() => onQuickView?.(selectedVariant.variantId)}
            />

            {/* 信息 */}
            <div className="info flex-1">
                {/* name */}
                <ListProductName
                    className={'pointer'}
                    canJump={false}
                    product={selectedVariant as any}
                    onClick={() => onQuickView?.(selectedVariant.variantId)}
                />

                {/* prices */}
                <div className="prices">
                    {selectedVariant?.tradeIn?.tradeInType === 'points'
                        ? (
                            <div className={'flex flex-middle'}>
                                <IconPoints size={18} className="g-icon-points"/>
                                <span className="points">{selectedVariant?.tradeIn?.points}</span>
                            </div>
                        )
                        : (
                            <div>
                                <strong>
                                    {
                                        (selectedVariant?.tradeIn?.free ? t('web_price_free_upper') : selectedVariant?.tradeIn?.dealPriceExchangeDisplay)
                                    }
                                </strong>
                            </div>
                        )
                    }
                    <del className="del-price">{selectedVariant?.shopPriceExchangeDisplay}</del>
                </div>

                {/* options */}
                {variants.length > 1 && (
                    <div className="options-wrap">
                        <p className="title">{t('web_cart_bonus_more_options')}</p>
                        <div className="options flex">
                            {variants.map((vItem, vIndex) => {
                                if (optionType == 'image') {
                                    return <div
                                        key={vItem.variantId}
                                        className={cn(
                                            'option-image-wrap',
                                            { 'disabled': !checkVariantOnSale(vItem) },
                                            { 'on': vItem.variantId == selectedVariant.variantId },
                                        )}
                                    >
                                        <div
                                            className={'option-image'}
                                            onClick={() => onSelectOption(vItem)}
                                        >
                                            <ListProductImage
                                                background={false}
                                                product={vItem}
                                                imageSize={isPhone ? 40 : 38}
                                                fixed
                                                canJump={false}
                                                showTag={false}
                                                canShowSoldOut={false}
                                            />
                                        </div>
                                    </div>
                                }

                                return (
                                    <div
                                        key={vItem.variantId}
                                        className={cn(
                                            'option-text-wrap',
                                            { 'disabled': !checkVariantOnSale(vItem) },
                                            { 'on': vItem.variantId == selectedVariant.variantId },
                                        )}
                                    >
                                        <span
                                            className="option-text"
                                            onClick={() => onSelectOption(vItem)}
                                        >{vItem.tradeIn.optionText}</span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* m 勾选框 */}
            {showType === 'm' && (
                <div className={'flex flex-center flex-middle'}>
                    <div
                        className={cn('ipt')}
                        onClick={() => isStock && onSelectedBonusHandle(selectedVariant)}
                    >
                        <Iconfont
                            className={cn(
                                isSelectedOn ? 'icon-select1' : 'icon-radio-unchecked',
                                { 'out-of-stock': !isStock }
                            )}
                        />
                    </div>
                </div>
            )}

            {/* disable mask */}
            {!canSelect && (
                <div
                    className={cn(styles['disable-mask'], 'flex flex-middle flex-center pointer')}
                    onClick={() => onQuickView?.(selectedVariant.variantId)}
                >
                    <p className={styles.text}>{spendToUnlockBonusText}</p>
                </div>
            )}

        </div>
    );
};

export default BonusProdItem